"use client";

import { useState } from "react";
import { FaChevronLeft } from "react-icons/fa";
import { Loader2, Send } from "lucide-react";
import { Button } from "@/shadcn-components/ui/button";
import { Input } from "@/shadcn-components/ui/input";
import { Textarea } from "@/shadcn-components/ui/textarea";

interface ComposeMailScreenProps {
  onClose: () => void;
  onSend?: (to: string, subject: string, body: string) => void | Promise<void>;
  initialTo?: string;
  initialSubject?: string;
}

export default function ComposeMailScreen({
  onClose,
  onSend,
  initialTo = "Support",
  initialSubject = "",
}: ComposeMailScreenProps) {
  const [to, setTo] = useState(initialTo);
  const [subject, setSubject] = useState(initialSubject);
  const [body, setBody] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canSend = to.trim().length > 0 && (subject.trim().length > 0 || body.trim().length > 0);

  const handleSend = async () => {
    if (!canSend) {
      setError("Enter a recipient and a subject or message.");
      return;
    }

    setIsSending(true);
    setError(null);

    try {
      await onSend?.(to.trim(), subject.trim(), body.trim());
      onClose();
    } catch (err) {
      console.error("[ComposeMail] send failed:", err);
      setError("Failed to send. Check your connection and try again.");
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="flex h-full min-h-0 flex-col bg-background">
      <div className="flex shrink-0 items-center gap-2 border-b border-border px-3 py-2">
        <Button
          variant="ghost"
          size="icon"
          onClick={onClose}
          disabled={isSending}
          className="h-8 w-8 shrink-0"
        >
          <FaChevronLeft className="h-4 w-4 text-blue-400" />
        </Button>
        <span className="text-sm font-semibold">New message</span>
        <Button
          size="sm"
          className="ml-auto h-8 px-3 text-xs"
          disabled={isSending || !canSend}
          onClick={() => void handleSend()}
        >
          {isSending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <>
              <Send className="mr-1.5 h-3.5 w-3.5" />
              Send
            </>
          )}
        </Button>
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-3">
        {error && (
          <p className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-xs text-destructive">
            {error}
          </p>
        )}

        <div className="space-y-1">
          <label className="text-[11px] font-medium text-muted-foreground">To</label>
          <Input
            value={to}
            onChange={(e) => setTo(e.target.value)}
            disabled={isSending}
            placeholder="Support"
            className="h-10 text-sm"
          />
        </div>

        <div className="space-y-1">
          <label className="text-[11px] font-medium text-muted-foreground">Subject</label>
          <Input
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            disabled={isSending}
            placeholder="Subject"
            className="h-10 text-sm"
          />
        </div>

        <div className="flex min-h-0 flex-1 flex-col space-y-1">
          <label className="text-[11px] font-medium text-muted-foreground">Message</label>
          <Textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            disabled={isSending}
            placeholder="Write your message…"
            className="min-h-[200px] flex-1 resize-none text-sm"
          />
        </div>
      </div>
    </div>
  );
}
